/**
 * Shopify Cart Duplicate Product Checker
 *
 * This script checks if a product is already in the cart before adding it.
 * If found, shows a confirmation dialog asking the user if they want to add it again.
 * Only adds the product to the cart if the user confirms.
 */
const ShopifyDuplicateProductChecker = (function () {
  // Configuration
  const config = {
    messages: {
      duplicateConfirmation:
        'Dieses Produkt befindet sich bereits in Deinem Warenkorb. Möchtest Du wirklich einen weiteren Artikel hinzufügen? \n\nFalls Du Dir bei der Größe unsicher bist, wirf gerne einen Blick in die Größeninfo, um Deine ideale Größe zu finden und Retouren zu vermeiden.',
      confirmButton: 'Ja, hinzufügen',
      cancelButton: 'Abbrechen',
    },
  };

  // Selectors for finding elements
  const selectors = {
    addToCartForms: 'form[action$="/cart/add"]',
    addToCartButtons: '[data-add-to-cart]',
    variantSelector: '[name="id"]',
    complementaryProductContainer: '.dvrw-product-complementary__content',
    complementaryVariantSelects: 'select[name="dvrw-product-complementary-variants"]',
  };

  let isProcessing = false;

  /**
   * Initialize the duplicate product checker
   */
  function init() {
    // Intercept all add to cart form submissions
    document.querySelectorAll(selectors.addToCartForms).forEach((form) => {
      form.addEventListener('submit', handleFormSubmit, true);
    });

    // Listen for theme's custom add-to-cart event
    document.addEventListener('product:add', handleProductAddEvent, true);

    // Listen for complementary product selections
    document.addEventListener('change', function (event) {
      if (event.target.matches(selectors.complementaryVariantSelects)) {
        handleComplementarySelection(event);
      }
    });
  }

  /**
   * Common function to check for duplicates and handle adding to cart
   * @param {number} productId - The product ID to check
   * @param {string|number} variantId - The variant ID being added
   * @param {HTMLFormElement|null} form - The form element if applicable
   * @param {Object} options - Options object containing custom add function
   */
  function checkAndAddToCart(productId, variantId, form, options) {
    // If we can't determine the product ID, just proceed with adding to cart

    if (!productId) {
      options.addToCartFunction(true);
      return;
    }

    // Check if the product is already in the cart
    isProductInCart(productId)
      .then((inCart) => {
        if (inCart) {
          // Product is in cart - show confirmation
          showConfirmationModal().then((confirmed) => {
            options.addToCartFunction(confirmed);
          });
        } else {
          // Product not in cart - add it
          options.addToCartFunction(true);
        }
      })
      .catch((error) => {
        console.error('Error checking cart:', error);
        // On error, proceed with adding to cart
        options.addToCartFunction(true);
      });
  }

  /**
   * Handle complementary product selection
   * @param {Event} event - The change event from the select element
   */
  function handleComplementarySelection(event) {
    const selectElement = event.target;
    const variantId = selectElement.value;

    if (!variantId) return; // Skip if no variant is selected

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    if (isProcessing) return;
    isProcessing = true;

    // Get the product container to find product ID
    const productContainer = selectElement.closest(selectors.complementaryProductContainer);
    let productId = null;

    // More robust product ID detection
    if (productContainer) {
      // Try to get from data-app-products attribute
      if (productContainer.dataset.appProducts) {
        // Clean up the product ID in case it has extra characters
        const cleanId = String(productContainer.dataset.appProducts).replace(/\D+/g, '');
        productId = parseInt(cleanId, 10);
      }

      // If that failed, try to find product ID from other sources
      if (!productId && productContainer.dataset.productId) {
        productId = parseInt(productContainer.dataset.productId, 10);
      }

      // Backup: try to get from variant data if we have it
      if (!productId && variantId) {
        // You might need to fetch the variant data from the Shopify API
        // or have it pre-loaded in a data attribute
      }
    }

    // Check if this is a duplicate item in cart
    checkAndAddToCart(productId, variantId, null, {
      // Custom add function for complementary products
      addToCartFunction: function (confirmed) {
        if (confirmed) {
          addComplementaryItem(variantId);
        } else {
          // Reset the select element
          selectElement.selectedIndex = 0;
          isProcessing = false;
        }
      },
    });
  }

  /**
   * Add a complementary item to the cart
   * @param {string|number} variantId - The variant ID to add
   */
  function addComplementaryItem(variantId) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Reset select dropdown
        document.querySelectorAll(selectors.complementaryVariantSelects).forEach((select) => {
          if (select.value === variantId) {
            select.selectedIndex = 0;
          }
        });

        // Trigger cart update events
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: {
              product: data,
              variant: variantId,
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { cart: data },
          })
        );

        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Open cart if theme supports it
        try {
          if (window.theme && window.theme.CartDrawer) {
            document.dispatchEvent(new CustomEvent('cart:open'));
          }
        } catch (e) {
          console.log('Note: Could not open cart drawer');
        }
      })
      .catch((error) => {
        console.error('Error adding complementary item to cart:', error);
      })
      .finally(() => {
        isProcessing = false;
      });
  }

  /**
   * Handle the theme's product:add event
   * @param {Event} evt - The product:add event
   */
  function handleProductAddEvent(evt) {
    evt.stopImmediatePropagation();
    evt.preventDefault();

    const addSource = evt.detail ? evt.detail.element : null;
    if (!addSource) return;

    const form = addSource.closest('form');
    if (!form) return;

    handleFormSubmit({
      currentTarget: form,
      preventDefault: () => {},
      stopImmediatePropagation: () => {},
    });
  }

  /**
   * Handle the form submission
   * @param {Event} evt - The form submit event
   */
  function handleFormSubmit(evt) {
    // Prevent the default form submission and stop event propagation
    evt.preventDefault();
    evt.stopImmediatePropagation();

    if (isProcessing) return;
    isProcessing = true;

    const form = evt.currentTarget;
    const addToCartButton = form.querySelector(selectors.addToCartButtons);
    if (addToCartButton) {
      addToCartButton.classList.add('btn--loading');
    }

    const productId = getProductId(form);
    const variantId = getSelectedVariantId(form);

    checkAndAddToCart(productId, variantId, form, {
      // Custom add function for regular products
      addToCartFunction: function (confirmed) {
        if (confirmed) {
          submitForm(form);
        } else {
          if (addToCartButton) {
            addToCartButton.classList.remove('btn--loading');
          }
          isProcessing = false;
        }
      },
    });
  }

  /**
   * Get the product ID from the form or page
   * @param {HTMLFormElement} form - The product form
   * @returns {number|null} - The product ID or null if not found
   */
  function getProductId(form) {
    try {
      // 1. Try to get from product container data attribute
      const productContainer = form.closest('[data-product-id]');
      if (productContainer && productContainer.dataset.productId) {
        return parseInt(productContainer.dataset.productId, 10);
      }

      // 2. Try to get from meta tags
      const productMetaTag = document.querySelector('meta[property="og:product_id"]');
      if (productMetaTag && productMetaTag.content) {
        return parseInt(productMetaTag.content, 10);
      }

      // 3. Try to extract from URL for product pages
      const pathMatch = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
      if (pathMatch && window.meta && window.meta.product) {
        return window.meta.product.id;
      }

      // 4. Try to get from window global variables that themes sometimes set
      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product
      ) {
        return window.ShopifyAnalytics.meta.product.id;
      }

      // 5. Try to get from variant JSON
      const variantJsonEl = document.querySelector('[data-variant-json]');
      if (variantJsonEl) {
        const variantId = getSelectedVariantId(form);
        if (variantId) {
          try {
            const variants = JSON.parse(variantJsonEl.textContent);
            if (Array.isArray(variants)) {
              const variant = variants.find((v) => v.id.toString() === variantId.toString());
              if (variant && variant.product_id) {
                return variant.product_id;
              }
            }
          } catch (e) {
            console.error('Error parsing variant JSON:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error getting product ID:', error);
    }

    return null;
  }

  /**
   * Get the selected variant ID from the form
   * @param {HTMLFormElement} form - The form element
   * @returns {string|null} - The selected variant ID or null
   */
  function getSelectedVariantId(form) {
    // Check the form for variant inputs
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      return variantInput.value;
    }

    const variantSelect = form.querySelector('select[name="id"]');
    if (variantSelect) {
      return variantSelect.value;
    }

    const variantRadio = form.querySelector('input[name="id"]:checked');
    if (variantRadio) {
      return variantRadio.value;
    }

    // Check URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      return variantId;
    }

    return null;
  }

  /**
   * Check if the product is already in the cart
   * @param {number} productId - The product ID to check
   * @returns {Promise<boolean>} - Promise resolving to true if product is in cart
   */
  function isProductInCart(productId) {
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        if (!cart.items || !cart.items.length) {
          return false;
        }
        return cart.items.some((item) => item.product_id === parseInt(productId, 10));
      })
      .catch((error) => {
        console.error('Error checking cart:', error);
        return false; // Default to false on error
      });
  }

  /**
   * Show a confirmation modal and return a promise that resolves with the user's decision
   * @returns {Promise<boolean>} - Promise that resolves to true if user confirms, false otherwise
   */
  function showConfirmationModal() {
    return new Promise((resolve) => {
      // Create the modal container
      const modal = document.createElement('div');
      modal.className = 'duplicate-product-modal';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
      `;

      // Create the modal content
      const modalContent = document.createElement('div');
      modalContent.className = 'duplicate-product-modal-content';

      modalContent.style.cssText = `
        background-color: white;
        padding: 2rem;
        border-radius: 5px;
        max-width: 450px;
        width: 90%;
        text-align: center;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      `;

      // Add the message
      const message = document.createElement('p');
      message.textContent = config.messages.duplicateConfirmation;
      message.style.marginBottom = '2rem';
      message.style.whiteSpace = 'pre-line'; // Zeilen korrekt umbrechen

      // Create the button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: flex;
        justify-content: center;
        gap: 1rem;
      `;

      // Add the cancel button
      const cancelButton = document.createElement('button');
      cancelButton.textContent = config.messages.cancelButton;
      cancelButton.className = 'btn btn--secondary';
      cancelButton.style.cssText = `
        padding: 0.75rem 1rem;
        background-color: #f3f3f3;
        border: 1px solid #ddd;
        border-radius: 4px;
        cursor: pointer;
      `;
      cancelButton.addEventListener('click', () => {
        closeModal();
        resolve(false);
      });

      // Add the confirm button
      const confirmButton = document.createElement('button');
      confirmButton.textContent = config.messages.confirmButton;
      confirmButton.className = 'btn';
      confirmButton.style.cssText = `
        padding: 0.75rem 1.5rem;
        background-color: #000;
        color: white;
        border: 1px solid #000;
        border-radius: 4px;
        cursor: pointer;
      `;
      confirmButton.addEventListener('click', () => {
        closeModal();
        resolve(true);
      });

      // Assemble the modal
      buttonContainer.appendChild(cancelButton);
      buttonContainer.appendChild(confirmButton);
      modalContent.appendChild(message);
      modalContent.appendChild(buttonContainer);
      modal.appendChild(modalContent);

      // Add to the document and prevent scrolling
      document.body.appendChild(modal);
      document.body.style.overflow = 'hidden';

      // Close on background click
      modal.addEventListener('click', (event) => {
        if (event.target === modal) {
          closeModal();
          resolve(false);
        }
      });

      // Function to close the modal
      function closeModal() {
        document.body.removeChild(modal);
        document.body.style.overflow = '';
      }
    });
  }

  /**
   * Submit the form using AJAX
   * @param {HTMLFormElement} form - The form to submit
   */
  function submitForm(form) {
    const formData = new FormData(form);
    const addToCartButton = form.querySelector(selectors.addToCartButtons);

    // Use the theme's AJAX add-to-cart fetch request
    fetch('/cart/add.js', {
      method: 'POST',
      body: new URLSearchParams(formData),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && data.status !== 200) {
          console.error('Error adding to cart:', data);
          return;
        }

        // Trigger theme's cart update events
        if (window.theme && window.theme.cart) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product: data,
              },
            })
          );
        }

        // Try to open cart drawer if it exists
        try {
          if (window.theme && window.theme.CartDrawer) {
            document.dispatchEvent(new CustomEvent('cart:open'));
          }
        } catch (e) {
          console.log('Note: Could not open cart drawer');
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      })
      .finally(() => {
        if (addToCartButton) {
          addToCartButton.classList.remove('btn--loading');
        }
        isProcessing = false;
      });
  }

  return {
    init,
    isProductInCart,
    checkAndAddToCart,
  };
})();

// Initialize when the document is ready
document.addEventListener('DOMContentLoaded', function () {
  const hasAddToCartButtons = Boolean(document.querySelector('[data-add-to-cart]'));
  const hasComplementaryProducts = Boolean(
    document.querySelector('.dvrw-product-complementary__content')
  );

  window.ShopifyDuplicateProductChecker = ShopifyDuplicateProductChecker;

  // Initialize if we have either regular products or complementary products
  if (hasAddToCartButtons || hasComplementaryProducts) {
    ShopifyDuplicateProductChecker.init();
  }
});
